import { SpotlightItem, SpotlightItemType } from '@thoughtspot/global-nav';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { useCallback, useMemo, useState } from 'react';
import {
    GetAnswersListQuery,
    useGetAnswersListQuery as _useGetAnswersListQuery,
} from '../../services/generated/graphql-types';

type AnswerObject = NonNullable<
    NonNullable<GetAnswersListQuery['getAnswerList']['objects']>[number]
>;

/**
 * Lazily fetches answers visible to the user and converts them to
 * SpotlightItem[]. The fetch is triggered by calling activate().
 *
 * Used when the user selects the "Answers" filter in the command palette.
 */
export function useSpotlightAnswerItems() {
    const [isActive, setIsActive] = useState(false);

    const { data, loading, refetch } = _useGetAnswersListQuery({
        fetchPolicy: 'network-only',
        skip: !isActive,
        variables: {
            params: {
                autoCreated: false,
                showHidden: false,
                sortAscending: false,
                fetchPermissions: false,
                batchSize: 100,
            },
        },
    });

    const answerItems = useMemo<SpotlightItem[]>(() => {
        const objects = data?.getAnswerList?.objects;
        if (!objects) return [];
        return objects.map((answer: AnswerObject) => {
            const authorLabel = answer.authorDisplayName || answer.authorName;
            return {
                id: `answer-spotlight-${answer.id}`,
                label: answer.name,
                description: authorLabel ? `by ${authorLabel}` : undefined,
                icon: IconID.ANSWER,
                type: SpotlightItemType.NAVIGATION,
                path: `/saved-answer/${answer.id}/`,
                keywords: ['answer', 'query'],
            };
        });
    }, [data]);

    const activate = useCallback(() => {
        if (isActive) {
            refetch();
        } else {
            setIsActive(true);
        }
    }, [isActive, refetch]);

    return { answerItems, answerItemsLoading: loading, activate };
}
